import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../redux/store';
import { PlannedMeal, markMealAsCooked, unmarkMealAsCooked } from '../../redux/mealPlanSlice';
import { DeductionSummary, restoreDeductionSnapshot } from '../../redux/inventorySlice';
import { endSession, setScreen } from '../../redux/cookingSessionSlice';
import { useTranslation } from 'react-i18next';

interface DoneViewProps {
  meal: PlannedMeal;
  userId?: string;
  deductionSummary: DeductionSummary[] | null;
}

const DoneView: React.FC<DoneViewProps> = ({ meal, userId, deductionSummary }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const [isRestoring, setIsRestoring] = useState(false);

  useEffect(() => {
    dispatch(markMealAsCooked({ mealId: meal.id }));
  }, [meal.id, dispatch]);

  const handleGoToInventory = () => {
    dispatch(endSession());
    router.push('/(app)/inventory');
  };

  const handleUndo = async () => {
    setIsRestoring(true);
    try {
      await dispatch(restoreDeductionSnapshot({ userId })).unwrap();
      dispatch(unmarkMealAsCooked({ mealId: meal.id }));
      dispatch(setScreen('cooking'));
    } catch (error) {
      console.error('Error restoring deduction snapshot:', error);
      setIsRestoring(false);
    }
  };

  const hasDeductions = deductionSummary && deductionSummary.length > 0;

  return (
    <View style={styles.container}>
      <SafeAreaView style={styles.safeArea} />

      <View style={styles.content}>
        {/* Success circle */}
        <View style={styles.successCircle}>
          <Ionicons name="checkmark" size={32} color="#78A55A" />
        </View>

        {/* "Bon appétit !" */}
        <Text style={styles.title}>{t('cooking.enjoy')}</Text>

        {/* Subtitle */}
        <Text style={styles.subtitle}>{t('cookingPlan.pantryAutoUpdated')}</Text>

        {/* Deduction card */}
        {hasDeductions && (
          <View style={styles.deductionCard}>
            <Text style={styles.deductionTitle}>{t('cookingPlan.removedFromPantry')}</Text>
            {deductionSummary!.map((item, idx) => (
              <View key={idx} style={styles.deductionRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.deductionName}>{item.ingredientName}</Text>
                  <View style={styles.deductionQuantities}>
                    <Text style={styles.deductionOldQty}>
                      {item.previousQuantity} {item.previousUnit ?? ''}
                    </Text>
                    <Text style={styles.deductionArrow}>→</Text>
                    <Text style={styles.deductionNewQty}>
                      {item.newQuantity !== undefined ? item.newQuantity : t('cookingPlan.depleted')} {item.newUnit ?? ''}
                    </Text>
                  </View>
                </View>
              </View>
            ))}
            <Text style={styles.deductionNote}>{t('cookingPlan.staplesNotTracked')}</Text>
          </View>
        )}

        {!hasDeductions && (
          <View style={styles.deductionCard}>
            <Text style={styles.deductionTitle}>{t('cookingPlan.removedFromPantry')}</Text>
            <Text style={styles.noDeductionText}>
              {t('cookingPlan.noItemsMatched')}
            </Text>
            <Text style={styles.deductionNote}>{t('cookingPlan.staplesNotTracked')}</Text>
          </View>
        )}
      </View>

      {/* Footer */}
      <SafeAreaView style={styles.footer}>
        <LinearGradient
          colors={['#864e2c', '#a36642']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.ctaButton}
        >
          <TouchableOpacity onPress={handleGoToInventory} style={styles.ctaButtonContent}>
            <Text style={styles.ctaButtonText}>{t('cookingPlan.viewPantry')}</Text>
          </TouchableOpacity>
        </LinearGradient>

        {hasDeductions && (
          <TouchableOpacity
            onPress={handleUndo}
            disabled={isRestoring}
            style={styles.undoButton}
          >
            <Text style={styles.undoButtonText}>{t('cookingPlan.undoUpdate')}</Text>
          </TouchableOpacity>
        )}
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fbf9f6',
  },
  safeArea: {
    backgroundColor: '#fbf9f6',
  },

  content: {
    flex: 1,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Success circle
  successCircle: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: '#eaf0e2',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },

  // Title
  title: {
    fontSize: 25,
    fontWeight: '400',
    color: '#1b1c1a',
    fontFamily: 'Newsreader-Italic',
    fontStyle: 'italic',
    marginBottom: 4,
  },

  // Subtitle
  subtitle: {
    fontSize: 14,
    fontWeight: '400',
    color: '#52443c',
    textAlign: 'center',
    fontFamily: 'Inter-Regular',
    marginBottom: 24,
  },

  // Deduction card
  deductionCard: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 16,
    width: '100%',
    shadowColor: 'rgba(82, 68, 60, 0.08)',
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 8,
    elevation: 2,
  },
  deductionTitle: {
    fontSize: 13,
    fontWeight: '500',
    color: '#85746b',
    marginBottom: 12,
    fontFamily: 'Inter-Medium',
  },
  deductionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 10,
  },
  deductionName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1b1c1a',
    fontFamily: 'Inter-Medium',
    marginBottom: 4,
  },
  deductionQuantities: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  deductionOldQty: {
    fontSize: 12,
    fontWeight: '400',
    color: '#85746b',
    textDecorationLine: 'line-through',
    fontFamily: 'Inter-Regular',
  },
  deductionArrow: {
    fontSize: 12,
    color: '#85746b',
    fontFamily: 'Inter-Regular',
  },
  deductionNewQty: {
    fontSize: 12,
    fontWeight: '500',
    color: '#864e2c',
    fontFamily: 'Inter-Medium',
  },
  noDeductionText: {
    fontSize: 13,
    fontWeight: '400',
    color: '#85746b',
    fontFamily: 'Inter-Regular',
    marginBottom: 8,
  },
  deductionNote: {
    fontSize: 11,
    fontWeight: '400',
    color: '#85746b',
    marginTop: 8,
    fontFamily: 'Inter-Regular',
  },

  // Footer
  footer: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: '#fbf9f6',
    gap: 8,
  },
  ctaButton: {
    borderRadius: 24,
    paddingVertical: 14,
    overflow: 'hidden',
  },
  ctaButtonContent: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
    fontFamily: 'Inter-Medium',
  },
  undoButton: {
    paddingVertical: 10,
    alignItems: 'center',
  },
  undoButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#864e2c',
    textAlign: 'center',
    fontFamily: 'Inter-Medium',
  },
});

export default DoneView;
